import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Key, Plus, Trash2, Copy, Check, RefreshCw, BarChart3,
  Eye, EyeOff, BookOpen, Zap, Hash, Activity,
  Shield, LayoutDashboard, Settings, Terminal, ExternalLink,
  Clock, AlertCircle, CheckCircle2, ChevronRight, LogOut, User,
  Globe, Code2,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import BrandLogo from './BrandLogo';

const API = process.env.REACT_APP_BACKEND_URL;
const DOMAIN = 'opensov.com';
const API_BASE = `https://api.${DOMAIN}`;

function getToken() { return localStorage.getItem('opensov-token'); }
function getUser() { return JSON.parse(localStorage.getItem('opensov-user') || '{}'); }
function authHeaders() { return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }; }

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-surface border border-line-2 rounded-md px-3 py-2 text-xs shadow-lg">
        <p className="text-content-2 mb-1">{label}</p>
        <p className="text-emerald-500 font-medium">{payload[0].value} requests</p>
        {payload[1] && <p className="text-purple-400 font-medium">{payload[1].value} tokens</p>}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const token = getToken();
  const user = getUser();

  useEffect(() => { if (!token) navigate('/auth?redirect=/dashboard'); }, [token, navigate]);

  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [page, setPage] = useState('overview');
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKey, setShowNewKey] = useState(null);
  const [copied, setCopied] = useState('');
  const [loading, setLoading] = useState(true);
  const [showKeys, setShowKeys] = useState({});

  const fetchKeys = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/keys`, { headers: authHeaders() });
      if (res.ok) setKeys(await res.json());
    } catch { /* ignore */ }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/keys/usage`, { headers: authHeaders() });
      if (res.ok) setUsage(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchKeys(), fetchUsage()]).then(() => setLoading(false));
  }, [fetchKeys, fetchUsage]);

  const createKey = async () => {
    const res = await fetch(`${API}/api/keys`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ name: newKeyName || 'Default' }),
    });
    if (res.ok) {
      const data = await res.json();
      setShowNewKey(data.api_key);
      setNewKeyName('');
      fetchKeys();
      fetchUsage();
    }
  };

  const revokeKey = async (keyId) => {
    await fetch(`${API}/api/keys/${keyId}`, { method: 'DELETE', headers: authHeaders() });
    fetchKeys(); fetchUsage();
  };

  const regenerateKey = async (keyId) => {
    const res = await fetch(`${API}/api/keys/${keyId}/regenerate`, { method: 'POST', headers: authHeaders() });
    if (res.ok) { const data = await res.json(); setShowNewKey(data.api_key); fetchKeys(); }
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleLogout = () => {
    localStorage.removeItem('opensov-token');
    localStorage.removeItem('opensov-user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-[#10B981] rounded-full animate-spin" />
      </div>
    );
  }

  const activeKeys = keys.filter(k => k.is_active);
  const revokedKeys = keys.filter(k => !k.is_active);

  const sidebarItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'keys', icon: Key, label: 'API Keys' },
    { id: 'usage', icon: BarChart3, label: 'Usage' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-app text-content flex" data-testid="api-dashboard">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-shrink-0 flex-col border-r border-line bg-app-alt">
        <div className="p-5 border-b border-line">
          <button onClick={() => navigate('/')} className="flex items-center gap-2.5 group" data-testid="sidebar-logo">
            <BrandLogo className="h-7" />
            <div>
              <span className="font-heading font-bold text-sm tracking-tight text-content group-hover:text-emerald-500 transition-colors">OpenSov</span>
              <span className="block text-[9px] text-content-3 uppercase tracking-wider">Developer Platform</span>
            </div>
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5">
          {sidebarItems.map(item => (
            <button key={item.id} onClick={() => setPage(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition-all
                ${page === item.id
                  ? 'bg-emerald-500/10 text-emerald-500 font-medium'
                  : 'text-content-2 hover:text-content hover:bg-overlay-hover'}`}
              data-testid={`nav-${item.id}`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}

          <div className="!mt-6 pt-4 border-t border-line space-y-0.5">
            <button onClick={() => navigate('/docs')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] text-content-2 hover:text-content hover:bg-overlay-hover transition-all"
              data-testid="nav-docs">
              <BookOpen className="w-4 h-4" /> Documentation
              <ExternalLink className="w-3 h-3 ml-auto opacity-40" />
            </button>
            <button onClick={() => navigate('/chat')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] text-content-2 hover:text-content hover:bg-overlay-hover transition-all"
              data-testid="nav-chat">
              <Terminal className="w-4 h-4" /> Playground
              <ExternalLink className="w-3 h-3 ml-auto opacity-40" />
            </button>
          </div>
        </nav>

        {/* User profile + logout */}
        <div className="p-4 border-t border-line">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-content font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-[10px] text-content-3 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-content-3 hover:text-red-400 hover:bg-red-500/5 transition-all"
            data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-app/95 backdrop-blur-xl border-b border-line">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-6" />
            <span className="font-heading font-bold text-sm">Developer</span>
          </div>
          <div className="flex gap-1">
            {sidebarItems.map(item => (
              <button key={item.id} onClick={() => setPage(item.id)}
                className={`p-2 rounded-md transition-colors ${page === item.id ? 'bg-emerald-500/10 text-emerald-500' : 'text-content-3'}`}>
                <item.icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-y-auto pt-14 md:pt-0">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-8 md:py-10">
          {/* New key banner */}
          {showNewKey && (
            <div className="mb-8 p-5 rounded-lg bg-emerald-500/[0.08] border border-emerald-500/25" data-testid="new-key-reveal">
              <div className="flex items-start gap-3 mb-3">
                <Shield className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-content mb-1">Your new API key</p>
                  <p className="text-xs text-content-2">Save this key now. You won't be able to see it again.</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 p-3 rounded-md bg-app border border-line">
                <code className="flex-1 text-sm text-emerald-500 font-mono truncate">{showNewKey}</code>
                <button onClick={() => copyText(showNewKey, 'newkey')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-500 text-xs font-medium hover:bg-emerald-500/20 transition-colors"
                  data-testid="copy-new-key-btn">
                  {copied === 'newkey' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'newkey' ? 'Copied' : 'Copy'}
                </button>
                <button onClick={() => setShowNewKey(null)} className="text-xs text-content-3 hover:text-content transition-colors px-2" data-testid="dismiss-key-btn">Done</button>
              </div>
            </div>
          )}

          {/* ─── Overview Page ─── */}
          {page === 'overview' && (
            <div>
              <div className="mb-10">
                <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-2">Dashboard</h1>
                <p className="text-sm text-content-2">Welcome back{user?.name ? `, ${user.name}` : ''}. Here's your API overview.</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {[
                  { label: 'Total Requests', value: usage?.total_requests || 0, icon: Zap, accent: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                  { label: 'Total Tokens', value: (usage?.total_tokens || 0).toLocaleString(), icon: Hash, accent: 'text-purple-400', bg: 'bg-purple-400/10' },
                  { label: 'Active Keys', value: activeKeys.length, icon: Key, accent: 'text-emerald-400', bg: 'bg-emerald-400/10' },
                  { label: 'Rate Limit', value: '100/day', icon: Activity, accent: 'text-amber-400', bg: 'bg-amber-400/10' },
                ].map((stat, i) => (
                  <div key={i} className="p-5 rounded-lg bg-surface border border-line hover:border-line-2 transition-all" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[11px] text-content-3 font-medium uppercase tracking-wider">{stat.label}</p>
                      <div className={`w-7 h-7 rounded-md ${stat.bg} flex items-center justify-center`}>
                        <stat.icon className={`w-3.5 h-3.5 ${stat.accent}`} />
                      </div>
                    </div>
                    <p className="text-2xl font-heading font-bold text-content tracking-tight">{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* API Endpoint Info */}
              <div className="rounded-lg border border-line bg-surface p-5 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-semibold text-content">API Base URL</h3>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-md bg-app border border-line">
                  <code className="flex-1 text-sm text-content-2 font-mono">{API_BASE}/v1</code>
                  <button onClick={() => copyText(`${API_BASE}/v1`, 'base-url')}
                    className="p-1.5 rounded text-content-3 hover:text-emerald-500 transition-colors" data-testid="copy-base-url-btn">
                    {copied === 'base-url' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Quick Start */}
              <div className="rounded-lg border border-line bg-surface overflow-hidden mb-6">
                <div className="flex items-center justify-between p-5 border-b border-line">
                  <div>
                    <h3 className="text-sm font-semibold text-content mb-0.5">Quick Start</h3>
                    <p className="text-[11px] text-content-3">Make your first API call with streaming</p>
                  </div>
                  <button onClick={() => navigate('/docs')}
                    className="text-xs text-emerald-500 hover:underline flex items-center gap-1 font-medium" data-testid="quick-start-docs-link">
                    View docs <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="p-5 relative group">
                  <button onClick={() => copyText(`curl -X POST ${API_BASE}/v1/chat/completions \\\n  -H "Authorization: Bearer YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"opensov-1","messages":[{"role":"user","content":"Hello"}],"stream":true}'`, 'quickstart')}
                    className="absolute top-3 right-3 p-2 rounded-md bg-overlay text-content-3 hover:text-content opacity-0 group-hover:opacity-100 transition-all z-10" data-testid="copy-quickstart-btn">
                    {copied === 'quickstart' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  <pre className="text-[12px] text-content-2 font-mono leading-relaxed overflow-x-auto">
{`curl -X POST ${API_BASE}/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "opensov-1",
    "messages": [{"role": "user", "content": "Hello"}],
    "stream": true
  }'`}
                  </pre>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="rounded-lg border border-line bg-surface overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-line">
                  <h3 className="text-sm font-semibold text-content">Recent Activity</h3>
                  {usage?.recent_logs?.length > 0 && (
                    <button onClick={() => setPage('usage')} className="text-xs text-emerald-500 hover:underline flex items-center gap-1 font-medium" data-testid="view-all-activity-btn">
                      View all <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {usage?.recent_logs?.length > 0 ? (
                  <div className="divide-y divide-white/[0.04]">
                    {usage.recent_logs.slice(0, 5).map((log, i) => (
                      <div key={i} className="flex items-center gap-4 px-5 py-3.5 text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400/60 flex-shrink-0" />
                        <span className="text-content-2 font-mono flex-1">{log.endpoint}</span>
                        <span className="text-content-3">{log.tokens_used} tokens</span>
                        <span className="text-content-3 tabular-nums">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Activity className="w-8 h-8 mx-auto mb-3 text-content-3" />
                    <p className="text-sm text-content-3 mb-1">No activity yet</p>
                    <p className="text-xs text-content-3">Create an API key and make your first call</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── API Keys Page ─── */}
          {page === 'keys' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-2">API Keys</h1>
                  <p className="text-sm text-content-2">Manage your API keys. Keep them secure — never expose in client-side code.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                    placeholder="Key name"
                    onKeyDown={e => e.key === 'Enter' && createKey()}
                    className="w-40 bg-app border border-line rounded-md px-3 py-2 text-sm text-content placeholder-content-3 focus:outline-none focus:border-emerald-500/40 transition-colors"
                    data-testid="key-name-input" />
                  <button onClick={createKey}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 text-btn-foreground text-xs font-semibold hover:bg-emerald-600 transition-all shadow-[0_0_18px_rgba(16,185,129,0.25)]"
                    data-testid="create-key-btn">
                    <Plus className="w-3.5 h-3.5" /> Create new key
                  </button>
                </div>
              </div>

              {/* Keys table */}
              <div className="rounded-lg border border-line bg-surface overflow-hidden">
                {activeKeys.length === 0 ? (
                  <div className="p-12 text-center">
                    <Key className="w-10 h-10 mx-auto mb-3 text-content-3" />
                    <p className="text-sm text-content-2 mb-1">No API keys yet</p>
                    <p className="text-xs text-content-3">Create your first key to start using the API.</p>
                  </div>
                ) : (
                  <table className="w-full" data-testid="api-keys-table">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left px-5 py-3.5 text-[11px] font-medium text-content-3 uppercase tracking-wider">Name</th>
                        <th className="text-left px-5 py-3.5 text-[11px] font-medium text-content-3 uppercase tracking-wider">Key</th>
                        <th className="text-left px-5 py-3.5 text-[11px] font-medium text-content-3 uppercase tracking-wider hidden sm:table-cell">Usage</th>
                        <th className="text-left px-5 py-3.5 text-[11px] font-medium text-content-3 uppercase tracking-wider hidden md:table-cell">Last Used</th>
                        <th className="text-right px-5 py-3.5 text-[11px] font-medium text-content-3 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {activeKeys.map(k => (
                        <tr key={k.key_id} className="hover:bg-overlay transition-colors" data-testid={`api-key-${k.key_id}`}>
                          <td className="px-5 py-4">
                            <p className="text-sm text-content font-medium">{k.name}</p>
                            <p className="text-[10px] text-content-3 mt-0.5">Created {new Date(k.created_at).toLocaleDateString()}</p>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <code className="text-xs text-content-2 font-mono">
                                {showKeys[k.key_id] ? k.api_key : k.api_key_masked}
                              </code>
                              <button onClick={() => setShowKeys(prev => ({ ...prev, [k.key_id]: !prev[k.key_id] }))}
                                className="text-content-3 hover:text-content-2 transition-colors" data-testid={`toggle-key-${k.key_id}`}>
                                {showKeys[k.key_id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </td>
                          <td className="px-5 py-4 hidden sm:table-cell">
                            <span className="text-xs text-content-3 tabular-nums">{k.total_requests} requests</span>
                          </td>
                          <td className="px-5 py-4 hidden md:table-cell">
                            <span className="text-xs text-content-3">{k.last_used ? new Date(k.last_used).toLocaleDateString() : 'Never'}</span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => copyText(k.api_key, k.key_id)}
                                className="p-2 rounded-md text-content-3 hover:text-emerald-500 hover:bg-overlay-hover transition-all" title="Copy"
                                data-testid={`copy-key-${k.key_id}`}>
                                {copied === k.key_id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                              <button onClick={() => regenerateKey(k.key_id)}
                                className="p-2 rounded-md text-content-3 hover:text-amber-400 hover:bg-overlay-hover transition-all" title="Regenerate"
                                data-testid={`regen-key-${k.key_id}`}>
                                <RefreshCw className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => revokeKey(k.key_id)}
                                className="p-2 rounded-md text-content-3 hover:text-red-400 hover:bg-overlay-hover transition-all" title="Revoke"
                                data-testid={`revoke-key-${k.key_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {revokedKeys.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xs font-medium text-content-3 uppercase tracking-wider mb-3">Revoked Keys</h3>
                  <div className="rounded-lg border border-line bg-surface/50 divide-y divide-white/[0.03]">
                    {revokedKeys.map(k => (
                      <div key={k.key_id} className="flex items-center justify-between px-5 py-3.5">
                        <div>
                          <p className="text-sm text-content-3">{k.name}</p>
                          <code className="text-[11px] text-content-3 font-mono">{k.api_key_masked}</code>
                        </div>
                        <span className="text-[9px] font-mono px-2 py-1 rounded bg-red-500/10 text-red-400/60 uppercase tracking-wider">Revoked</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Usage Page ─── */}
          {page === 'usage' && (
            <div>
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-2">Usage</h1>
                <p className="text-sm text-content-2">Monitor your API usage and token consumption.</p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { label: 'Requests Today', value: usage?.daily_stats?.length ? usage.daily_stats[usage.daily_stats.length - 1]?.requests || 0 : 0 },
                  { label: 'Tokens Today', value: usage?.daily_stats?.length ? (usage.daily_stats[usage.daily_stats.length - 1]?.tokens || 0).toLocaleString() : 0 },
                  { label: 'Remaining Today', value: Math.max(0, 100 - (usage?.daily_stats?.length ? usage.daily_stats[usage.daily_stats.length - 1]?.requests || 0 : 0)) },
                ].map((stat, i) => (
                  <div key={i} className="p-5 rounded-lg bg-surface border border-line" data-testid={`usage-stat-${i}`}>
                    <p className="text-[11px] text-content-3 uppercase tracking-wider mb-2">{stat.label}</p>
                    <p className="text-xl font-heading font-bold text-content">{stat.value}</p>
                  </div>
                ))}
              </div>

              {/* Chart with Recharts */}
              <div className="rounded-lg border border-line bg-surface p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-sm font-semibold text-content">Daily Requests</h3>
                  <span className="text-[10px] text-content-3 font-mono">Last 30 days</span>
                </div>
                {usage?.daily_stats?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={usage.daily_stats} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorReq" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="requests" stroke="#10B981" strokeWidth={2} fill="url(#colorReq)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-content-3">
                    <div className="text-center">
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No usage data yet</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Request Log */}
              <div className="rounded-lg border border-line bg-surface overflow-hidden">
                <div className="p-5 border-b border-line">
                  <h3 className="text-sm font-semibold text-content">Request Log</h3>
                </div>
                {usage?.recent_logs?.length > 0 ? (
                  <table className="w-full" data-testid="request-log-table">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left px-5 py-2.5 text-[10px] text-content-3 uppercase tracking-wider">Status</th>
                        <th className="text-left px-5 py-2.5 text-[10px] text-content-3 uppercase tracking-wider">Endpoint</th>
                        <th className="text-left px-5 py-2.5 text-[10px] text-content-3 uppercase tracking-wider hidden sm:table-cell">Model</th>
                        <th className="text-right px-5 py-2.5 text-[10px] text-content-3 uppercase tracking-wider">Tokens</th>
                        <th className="text-right px-5 py-2.5 text-[10px] text-content-3 uppercase tracking-wider">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.03]">
                      {usage.recent_logs.slice(0, 25).map((log, i) => (
                        <tr key={i} className="hover:bg-overlay transition-colors text-xs">
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 ${log.status === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {log.status === 'success' ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                              <span className="capitalize">{log.status}</span>
                            </span>
                          </td>
                          <td className="px-5 py-3 font-mono text-content-2">{log.endpoint}</td>
                          <td className="px-5 py-3 text-content-3 hidden sm:table-cell">{log.model || 'opensov-1'}</td>
                          <td className="px-5 py-3 text-right text-content-3 tabular-nums">{log.tokens_used}</td>
                          <td className="px-5 py-3 text-right text-content-3 tabular-nums">{new Date(log.timestamp).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-12 text-center text-content-3">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">No API calls recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Settings Page ─── */}
          {page === 'settings' && (
            <div>
              <div className="mb-10">
                <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight mb-2">Settings</h1>
                <p className="text-sm text-content-2">Manage your account and preferences.</p>
              </div>

              {/* Account Info */}
              <div className="rounded-lg border border-line bg-surface overflow-hidden mb-6">
                <div className="p-5 border-b border-line">
                  <h3 className="text-sm font-semibold text-content flex items-center gap-2">
                    <User className="w-4 h-4 text-content-2" /> Account
                  </h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">Name</p>
                      <p className="text-sm text-content" data-testid="settings-name">{user?.name || 'Not set'}</p>
                    </div>
                  </div>
                  <div className="border-t border-line" />
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">Email</p>
                      <p className="text-sm text-content" data-testid="settings-email">{user?.email || 'Not set'}</p>
                    </div>
                  </div>
                  <div className="border-t border-line" />
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">User ID</p>
                      <p className="text-xs text-content-2 font-mono" data-testid="settings-user-id">{user?.user_id || 'N/A'}</p>
                    </div>
                    <button onClick={() => copyText(user?.user_id || '', 'uid')}
                      className="p-1.5 rounded text-content-3 hover:text-emerald-500 transition-colors" data-testid="copy-user-id-btn">
                      {copied === 'uid' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* API Config */}
              <div className="rounded-lg border border-line bg-surface overflow-hidden mb-6">
                <div className="p-5 border-b border-line">
                  <h3 className="text-sm font-semibold text-content flex items-center gap-2">
                    <Code2 className="w-4 h-4 text-content-2" /> API Configuration
                  </h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">Base URL</p>
                      <code className="text-sm text-emerald-500 font-mono" data-testid="settings-base-url">{API_BASE}/v1</code>
                    </div>
                    <button onClick={() => copyText(`${API_BASE}/v1`, 'settings-url')}
                      className="p-1.5 rounded text-content-3 hover:text-emerald-500 transition-colors" data-testid="copy-settings-url-btn">
                      {copied === 'settings-url' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="border-t border-line" />
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">Model</p>
                      <p className="text-sm text-content font-mono">opensov-1</p>
                    </div>
                  </div>
                  <div className="border-t border-line" />
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">Rate Limit</p>
                      <p className="text-sm text-content">100 requests/day per key</p>
                    </div>
                  </div>
                  <div className="border-t border-line" />
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-xs text-content-3 uppercase tracking-wider mb-1">Max Keys</p>
                      <p className="text-sm text-content">5 active keys per account</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Danger zone */}
              <div className="rounded-lg border border-red-500/20 bg-surface overflow-hidden">
                <div className="p-5 border-b border-red-500/10">
                  <h3 className="text-sm font-semibold text-red-400">Danger Zone</h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-content mb-0.5">Sign out</p>
                      <p className="text-xs text-content-3">Sign out of your account on this device.</p>
                    </div>
                    <button onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-all border border-red-500/20"
                      data-testid="settings-logout-btn">
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
